import { getRoleHeader } from "@components/table/members/members-table-utils";
import { getTransactionAmount, getTransactionDescription, isConsumedTransaction, isExpiredTransaction } from "@components/table/transactions/transactions-table-utils";
import { isGroupDetails } from "@custom-types/type-guards";
import { getMarkupAssigneeName } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getOpenSnapshotHref } from "@pages/project-details/project-snapshots/snapshot-utils";
import { getMarkupStatusLabel } from "@pages/project-details/project-markups/status/markup-status-utils";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { getSubscriptionPackageName, getSubscriptionStatus } from "@utils/credit-transaction-utils";
import { formatUserRoleType } from "@utils/data-display";
import { SNAPSHOTS_TABLE_COLUMN_LABELS } from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { exportDataByFormat } from "@utils/file-data-export-utils";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { getMemberNameById, getMembersMapById } from "@utils/member-utils";
import { capitalizeFirstLetter, stringifyCoordinates } from "@utils/string-utils";
import { getPrettyName } from "@utils/user-utils";
import { getCoordinateZUp } from "@utils/vector-utils";
import { getProjectArea, getProjectClientName, getProjectManager, getProjectSize, getSphereViewerUrl } from "@utils/project-utils";
import { getMarkupMeasurements } from "@utils/markups-utils";
import { convertDistance } from "@utils/unit-utils";
import { UiUtils } from "@stellar/utils";

/** The valid formats to export a file */

/**
 * Create file out of groups array and create a download handler
 */
export async function downloadGroupData(groups, formatDate, format, canAssignManagers) {
  const data = groups.map(group => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: group.id,
    Name: group.name,
    "Created At": formatDate(group.createdAt),
    // The count is meaningless for workspaces that cannot assign managers, so it is omitted there.
    ...(canAssignManagers && {
      Managers: isGroupDetails(group) ? UiUtils.DASH : group.numManagers
    }),
    Projects: getProjectNumbersInGroup(group) || UiUtils.DASH
    /* eslint-enable */
  }));
  await exportDataByFormat({
    data,
    fileName: "Groups List",
    format
  });
  return data.length;
}

/**
 * Create file out of teams array and create a download handler
 */
export async function downloadTeamData(teams, formatDate, format) {
  const data = teams.map(team => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: team.id,
    Name: team.name,
    Description: team.description,
    "Number of Members": team.memberCount,
    "Created At": formatDate(team.createdAt),
    Role: formatUserRoleType(team.role)
    /* eslint-enable */
  }));
  await exportDataByFormat({
    data,
    fileName: `${capitalizeFirstLetter(TEAMS_DISPLAY_NAME)} List`,
    format
  });
  return data.length;
}
/**
 * Create file out of project members array and create a download handler
 */
export async function downloadMembersData({
  fileName,
  members,
  subjectType,
  format,
  shouldExcludeRole = false
}) {
  const data = members.map(member => {
    const userData = {
      ID: FaroCoreApiUtils.Member.isTeam(member) ? member.email : member.identity,
      Name: FaroCoreApiUtils.Member.isMemberActive(member) ? getPrettyName(member) : UiUtils.DASH,
      Email: FaroCoreApiUtils.Member.isTeam(member) ? UiUtils.DASH : member.email,
      status: FaroCoreApiUtils.Member.isTeam(member) ? UiUtils.DASH : FaroCoreApiUtils.Member.getMemberStatus(member)
    };

    // Skipping Role column when subject type is team
    if (subjectType !== "team" && !shouldExcludeRole) {
      Object.assign(userData, {
        [getRoleHeader(subjectType)]: formatUserRoleType(member.role)
      });
    }
    return userData;
  });
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}
/**
 * Columns excluded from map view exports because the map data source
 * (streamProjects) does not include these information.
 */
export const MAP_EXPORT_EXCLUDED_COLUMNS = ["Project Manager Name", "Project Manager Email"];

/**
 * Create file out of project array and create a download handler
 */
export async function downloadProjectsData({
  fileName,
  projects,
  formatDate,
  format,
  columnsToExclude,
  areaUnit
}) {
  const allColumns = projects.map(project => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    "Project Name": project.name,
    Client: getProjectClientName(project),
    Area: getProjectArea(project, areaUnit),
    "Project Manager Name": projectManagerName(project),
    "Project Manager Email": getProjectManager(project)?.email || UiUtils.DASH,
    Edited: formatDate(project.modifiedAt),
    "Created At": formatDate(project.createdAt),
    "Last Captured": project.lastCapturedAt ? formatDate(project.lastCapturedAt) : UiUtils.DASH,
    Size: getProjectSize(project),
    Coordinates: project.position ? stringifyCoordinates(project.position) : UiUtils.DASH,
    "Project Link": getSphereViewerUrl({
      projectId: project.id
    }).href
    /* eslint-enable */
  }));
  const data = columnsToExclude?.length ? filterColumns({
    allColumns,
    columnsToExclude
  }) : allColumns;
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}
/**
 * Create file out of snapshots array and create a download handler
 */
export async function downloadSnapshotsData({
  snapshots,
  fileName,
  formatDate,
  format,
  defaultOpenProjectTarget
}) {
  const data = snapshots.map(snapshot => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: snapshot.id,
    [SNAPSHOTS_TABLE_COLUMN_LABELS.name]: snapshot.name,
    [SNAPSHOTS_TABLE_COLUMN_LABELS.accessLevel]: FaroCoreApiUtils.Project.AccessLevelDisplayNames[snapshot.accessLevel],
    [SNAPSHOTS_TABLE_COLUMN_LABELS.created]: formatDate(snapshot.createdAt),
    "Snapshot Link": getOpenSnapshotHref({
      snapshotId: snapshot.id,
      openTarget: defaultOpenProjectTarget
    })
    /* eslint-enable */
  }));
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}
/** Removes the specified columns from each row. */
function filterColumns({
  allColumns,
  columnsToExclude
}) {
  const excludedSet = new Set(columnsToExclude);
  return allColumns.map(row => Object.fromEntries(Object.entries(row).filter(([key]) => !excludedSet.has(key))));
}

/**
 * Returns the project manager name
 * If the project manager is active, it will return the name otherwise it will return DASH
 */
function projectManagerName(project) {
  const projectManager = getProjectManager(project);
  if (projectManager && FaroCoreApiUtils.Member.isMemberActive(projectManager)) {
    return getPrettyName(projectManager);
  }
  return UiUtils.DASH;
}

/**
 * Interface for the global pose of a markup with Z as the up-axis to be
 * in-line with the Sphere Viewer.
 * It contains the X, Y, and Z coordinates of the global pose without scale or rotation.
 * If the global pose is not available, the values will be undefined.
 */

/**
 *
 * @returns the global pose of a markup in the passed units of measurement
 * @param markup The markup to get the global pose from
 * @param unitMeasurement The unit of measurement to convert the global pose values to
 */
function getMarkupGlobalPose(markup, unitMeasurement) {
  if (!markup.globalPose) {
    return {
      GlobalPoseX: undefined,
      GlobalPoseY: undefined,
      GlobalPoseZ: undefined
    };
  }
  const {
    x,
    y,
    z
  } = getCoordinateZUp(markup.globalPose.pos);
  const convertedX = convertDistance(x, unitMeasurement);
  const convertedY = convertDistance(y, unitMeasurement);
  const convertedZ = convertDistance(z, unitMeasurement);
  return {
    GlobalPoseX: convertedX,
    GlobalPoseY: convertedY,
    GlobalPoseZ: convertedZ
  };
}

/** Props for getting markup measurement columns */

/** Columns for markup measurements */

/**
 * Measurement columns without a value, so that every exported row keeps the same
 * set of columns whether or not the markup has a measurement to report.
 */
const EMPTY_MARKUP_MEASUREMENT_COLUMNS = {
  "Measurement Area": undefined,
  "Measurement Length": undefined,
  "Measurement Horizontal": undefined,
  "Measurement Vertical": undefined,
  "Measurement X": undefined,
  "Measurement Y": undefined,
  "Measurement Z": undefined
};

/**
 * @returns The measurement values of a measurement markup.
 * If it's not a measurement markup, or its world transform has not been computed yet,
 * the values will be undefined.
 */
function getMarkupMeasurementColumns({
  markup,
  worldTransformCache,
  unitMeasurement
}) {
  const measurePolygon = markup.measurePolygon;
  const worldTransform = measurePolygon ? worldTransformCache[measurePolygon.id] : undefined;

  // The cache is only populated for the loaded part of the project, so a measurement can be
  // missing from it. Without its world transform the measurement cannot be expressed in world
  // coordinates, so the columns are left empty instead of exporting local-space values.
  if (!measurePolygon || !worldTransform) {
    return EMPTY_MARKUP_MEASUREMENT_COLUMNS;
  }
  const measurements = getMarkupMeasurements({
    measurePolygon,
    worldMatrix4Tuple: worldTransform.worldMatrix,
    unitMeasurement
  });
  return {
    "Measurement Area": measurements.area,
    "Measurement Length": measurements.length,
    "Measurement Horizontal": measurements.horizontal,
    "Measurement Vertical": measurements.vertical,
    "Measurement X": measurements.x,
    "Measurement Y": measurements.y,
    "Measurement Z": measurements.z
  };
  /* eslint-enable @typescript-eslint/naming-convention */
}

/**
 * Create file out of markups array and create a download handler
 */
export async function downloadMarkupData({
  markups,
  companyMembers,
  projectMembers,
  fileName,
  markupHref,
  formatDate,
  format,
  worldTransformCache,
  unitMeasurement
}) {
  const data = markups.map(markup => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: markup.id,
    Name: markup.name,
    Assignee: getMarkupAssigneeName({
      assignee: markup.assignee,
      companyMembers,
      projectMembers
    }),
    "Created At": formatDate(markup.createdAt),
    "Created By": getMemberNameById({
      memberId: markup.createdBy,
      companyMembers,
      projectMembers
    }),
    "Due Date": markup.dueDate?.value && formatDate(markup.dueDate.value),
    Status: getMarkupStatusLabel(markup.status?.value),
    "Status Last Modified Date": markup.status?.modifiedAt && formatDate(markup.status.modifiedAt),
    Tags: markup.labels?.map(label => label.name).join(", "),
    "Number of Attachments": markup.attachmentsCount,
    Description: markup.descr,
    Area: markup.areaSection?.name,
    Capture: markup.section?.name,
    Url: markupHref(markup),
    ...getMarkupGlobalPose(markup, unitMeasurement),
    ...getMarkupMeasurementColumns({
      markup,
      worldTransformCache,
      unitMeasurement
    })
    /* eslint-enable */
  }));
  if (data.length === 0) {
    return 0;
  }
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}
/** Create file out of transactions array and create a download handler */
export async function downloadTransactionsData({
  transactions,
  companyMembers,
  projects,
  fileName,
  formatDate,
  format
}) {
  // Cache the project names using the project ID as the key
  const projectMap = new Map(projects.map(project => [project.id, project.name]));
  const membersMap = getMembersMapById(companyMembers);
  const data = transactions.map(transaction => {
    const member = transaction.context.userId ? membersMap.get(transaction.context.userId) : undefined;
    const tokenAmount = getTransactionAmount(transaction);
    const amountToShow = isConsumedTransaction(transaction) || isExpiredTransaction(transaction) ? -Math.abs(tokenAmount) : Math.abs(tokenAmount);
    return {
      /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
      Date: formatDate(transaction.timestamp),
      "User Name": member?.name ?? UiUtils.DASH,
      "User Email": member?.email ?? UiUtils.DASH,
      Description: getTransactionDescription(transaction),
      "Project ID": transaction.context.projectId ?? UiUtils.DASH,
      "Project Name": transaction.context.projectId ? projectMap.get(transaction.context.projectId) : UiUtils.DASH,
      [capitalizeFirstLetter(CREDITS_DISPLAY_NAME)]: amountToShow
      /* eslint-enable */
    };
  });
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}

/** Create file out of credit subscriptions array and create a download handler */
export async function downloadPurchasesData({
  subscriptions,
  fileName,
  formatDate,
  format
}) {
  const creditsRemainingHeader = `${capitalizeFirstLetter(CREDITS_DISPLAY_NAME)} Remaining`;
  const data = subscriptions.map(subscription => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    Package: getSubscriptionPackageName(subscription),
    "Purchased Date": formatDate(subscription.startDate),
    "Expiration Date": formatDate(subscription.expiresAt),
    Status: getSubscriptionStatus(subscription),
    [creditsRemainingHeader]: subscription.availableCredit
    /* eslint-enable */
  }));
  await exportDataByFormat({
    data,
    fileName,
    format
  });
  return data.length;
}